import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AiReasoningStep, AiStoreActions, initialState } from '@signal-store/models/ai-store.model';
import { AgentRunThreadMessage } from '@system/search/cipo-ai/cipo-ai.component';
import { AiChatContextSelection, CipoAiService } from '@system/search/cipo-ai/cipo-ai.service';
import { HubHandlerSourceAngular } from '@models/system';
import { SignalRService } from '@shared/services';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { UserStore } from './user.store';

export const AiStore = signalStore(
  { providedIn: 'root' },
  withDevtools('ai'),
  withState(initialState),

  withComputed((store, userStore = inject(UserStore)) => ({
    isOpen: computed(() => userStore.cipoAI.assistantOpened()),
    context: computed(() => ({
      moduleId: userStore.menu.activeMenuItem()?.moduleId ?? null,
      contractId: userStore.contract()?.id ?? null,
    })),
  })),

  withMethods((store, cipoAiService = inject(CipoAiService), userStore = inject(UserStore)) => {
    const setLoading = (loading: boolean) => updateState(store, AiStoreActions.SetLoading, { loading });

    /**
     * Attaches the reasoning/tools-used steps captured for the current turn to the
     * last assistant message returned by the API, so the trace stays associated with
     * that specific message instead of being overwritten on the next question.
     */
    const attachReasoningStepsToLastAssistantMessage = (
      messages: AgentRunThreadMessage[],
      reasoningSteps: AiReasoningStep[],
    ): AgentRunThreadMessage[] => {
      if (reasoningSteps.length === 0) return messages;

      const lastAssistantIndex = messages.map(m => m.role).lastIndexOf('assistant');
      if (lastAssistantIndex === -1) return messages;

      return messages.map((message, index) =>
        index === lastAssistantIndex ? { ...message, reasoningSteps } : message,
      );
    };

    const loadThreads = rxMethod<void>(
      pipe(
        tap(() => setLoading(true)),
        switchMap(() =>
          cipoAiService.getThreads().pipe(
            tap(threads =>
              updateState(store, AiStoreActions.LoadThreadsSuccess, { threads: threads ?? [], loading: false }),
            ),
            catchError(() => {
              setLoading(false);
              return EMPTY;
            }),
          ),
        ),
      ),
    );

    return {
      /** Filters out non-meaningful ("thinking") steps from a message's reasoning trace. */
      meaningfulReasoningSteps(message: AgentRunThreadMessage): AiReasoningStep[] {
        return (message.reasoningSteps ?? []).filter(s => s.kind !== 'thinking');
      },

      updateSidebarWidth(sidebarWidth: number) {
        updateState(store, AiStoreActions.UpdateSidebarWidth, { sidebarWidth });
      },

      toggleOpen() {
        userStore.setAssistantOpened(!store.isOpen());
      },

      loadThreads,

      selectThread: rxMethod<string>(
        pipe(
          tap(() =>
            updateState(store, AiStoreActions.SelectThread, { loading: true, viewMode: 'thread', thread: null }),
          ),
          switchMap(threadId =>
            cipoAiService.getThread(threadId).pipe(
              tap(thread => updateState(store, AiStoreActions.SelectThreadSuccess, { thread, loading: false })),
              catchError(() => {
                setLoading(false);
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      askQuestion: rxMethod<{ text: string; contextSelection?: AiChatContextSelection }>(
        pipe(
          tap(({ text }) => {
            const userMessage: AgentRunThreadMessage = {
              role: 'user',
              createdAt: new Date().toISOString(),
              items: [{ text }],
            };

            const currentThread = store.thread();
            if (currentThread) {
              updateState(store, AiStoreActions.AskQuestion, {
                loading: true,
                thread: { ...currentThread, messages: [...(currentThread.messages ?? []), userMessage] },
                lastReasoningSteps: [],
              });
            } else {
              updateState(store, AiStoreActions.AskQuestion, {
                loading: true,
                thread: { threadId: '', createdAt: new Date().toISOString(), messages: [userMessage] },
                lastReasoningSteps: [],
              });
            }
          }),
          switchMap(({ text, contextSelection }) => {
            const threadId = store.thread()?.threadId || undefined;
            const isNewThread = !threadId;

            return cipoAiService.askQuestion(threadId, text, contextSelection).pipe(
              tap(response => {
                if (!response) {
                  setLoading(false);
                  return;
                }

                const currentSteps = store.reasoningSteps();
                const responseMessages = attachReasoningStepsToLastAssistantMessage(
                  response.messages ?? [],
                  currentSteps,
                );

                if (isNewThread) {
                  const userMessage: AgentRunThreadMessage = {
                    role: 'user',
                    createdAt: new Date().toISOString(),
                    items: [{ text }],
                  };
                  const messages = [userMessage, ...responseMessages];
                  updateState(store, AiStoreActions.AskQuestionSuccess, {
                    thread: { ...response, messages },
                    threads: [{ ...response, messages }, ...store.threads()],
                    loading: false,
                    lastReasoningSteps: currentSteps,
                    reasoningSteps: [],
                  });
                } else {
                  updateState(store, AiStoreActions.AskQuestionSuccess, {
                    thread: {
                      ...response,
                      messages: [...(store.thread()?.messages ?? []), ...responseMessages],
                    },
                    loading: false,
                    lastReasoningSteps: currentSteps,
                    reasoningSteps: [],
                  });
                }
              }),
              catchError(() => {
                updateState(store, AiStoreActions.ClearReasoningSteps, {
                  loading: false,
                  reasoningSteps: [],
                  lastReasoningSteps: [],
                });
                return EMPTY;
              }),
            );
          }),
        ),
      ),

      newThread() {
        updateState(store, AiStoreActions.NewThread, {
          thread: null,
          viewMode: 'thread',
          reasoningSteps: [],
          lastReasoningSteps: [],
        });
      },

      setViewMode(viewMode: 'thread' | 'threads') {
        updateState(store, AiStoreActions.SetViewMode, { viewMode });
        if (viewMode === 'threads') {
          loadThreads();
        }
      },

      deleteThread: rxMethod<string>(
        pipe(
          tap(() => setLoading(true)),
          switchMap(threadId =>
            cipoAiService.deleteThread(threadId).pipe(
              tap(() =>
                updateState(store, AiStoreActions.DeleteThreadSuccess, state => ({
                  loading: false,
                  threads: state.threads.filter(t => t.threadId !== threadId),
                  thread: state.thread?.threadId === threadId ? null : state.thread,
                })),
              ),
              catchError(() => {
                setLoading(false);
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };
  }),

  withHooks((store, signalRService = inject(SignalRService)) => ({
    onInit: () => {
      signalRService.registerHandler(HubHandlerSourceAngular, 'AiReasoningStep', (step: AiReasoningStep) => {
        updateState(store, AiStoreActions.AddReasoningStep, state => ({
          reasoningSteps: [...state.reasoningSteps, step],
        }));
      });
    },
  })),
);
